/**
 * Value formatting for the Console (§16.3, §16.5).
 *
 * Kept pure and DOM-free so it can be unit tested, and kept in one place so that
 * the canvas, the cards and the Truth View phrase the same fact the same way —
 * §16.4 requires the accessible table and the picture to agree, and two
 * formatting implementations is the easiest way to break that.
 *
 * Every function that formats an *absent* value returns explicit text rather
 * than an empty string. §5.8 makes honest uncertainty a product principle: a
 * blank cell reads as "zero", which is a different claim from "we do not know".
 */

import type { Freshness, SourcedValue } from '@autonomous-team/ui-projections';

/** Shown wherever a value is genuinely absent rather than unknown. */
export const NOT_APPLICABLE = '—';

export function formatDurationMs(ms: number | null): string {
  if (ms === null) return NOT_APPLICABLE;
  if (ms < 1000) return `${Math.max(0, Math.round(ms))} ms`;
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${String(minutes % 60).padStart(2, '0')}m`;
}

/** Age phrased for a human reading a status line, not a log. */
export function formatAgeMs(ms: number | null): string {
  if (ms === null) return 'never observed';
  if (ms < 1000) return 'just now';
  return `${formatDurationMs(ms)} ago`;
}

/** Cost figures are carried as integer micros to avoid float drift. */
export function formatMicros(micros: number | null): string {
  if (micros === null) return NOT_APPLICABLE;
  return `$${(micros / 1_000_000).toFixed(2)}`;
}

export function formatTokens(tokens: number | null): string {
  if (tokens === null) return NOT_APPLICABLE;
  if (tokens < 1000) return String(tokens);
  if (tokens < 1_000_000) return `${(tokens / 1000).toFixed(1)}k`;
  return `${(tokens / 1_000_000).toFixed(2)}M`;
}

export function formatPercent(fraction: number | null): string {
  if (fraction === null) return NOT_APPLICABLE;
  return `${Math.round(fraction * 100)}%`;
}

export function formatTimestamp(value: string | null): string {
  if (value === null) return 'never';
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return value;
  return new Date(ms).toISOString().replace('T', ' ').replace('.000Z', 'Z');
}

/** Truncates a digest for display while keeping it recognisable and copyable. */
export function shortDigest(digest: string | null): string {
  if (digest === null || digest.length === 0) return NOT_APPLICABLE;
  const body = digest.includes(':') ? (digest.split(':')[1] ?? digest) : digest;
  return body.length <= 12 ? body : `${body.slice(0, 12)}…`;
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return count === 1 ? `1 ${singular}` : `${count} ${plural}`;
}

/**
 * Words for a freshness level.
 *
 * §16.5 forbids colour as the only indicator, so every freshness badge renders
 * this text next to whatever colour it uses.
 */
export function freshnessLabel(freshness: Freshness): string {
  switch (freshness) {
    case 'fresh':
      return 'fresh';
    case 'stale':
      return 'STALE';
    case 'unknown':
      return 'UNKNOWN';
    default: {
      const exhaustive: never = freshness;
      throw new Error(`unhandled freshness ${String(exhaustive)}`);
    }
  }
}

/**
 * One line describing a sourced fact, including where it came from and how old.
 *
 * §16.4 requires board and execution state to be shown as separately sourced
 * facts with revision and freshness. Composing that sentence in one place stops
 * a screen from quietly dropping the provenance half of it.
 */
export function describeSourced(value: SourcedValue<string> | SourcedValue<number>): string {
  const shown = value.value === null ? 'unknown' : String(value.value);
  const revision = value.revision === null ? '' : ` at revision ${shortDigest(value.revision)}`;
  const observed = value.observedAt === null ? 'never observed' : `observed ${formatAgeMs(value.ageMs)}`;
  return `${shown} — from ${value.source}${revision}, ${observed} (${freshnessLabel(value.freshness)})`;
}

/** Screen-reader sentence for a stale or unknown source (§16.5: announced as text). */
export function stalenessSentence(label: string, freshness: Freshness, ageMs: number | null): string | null {
  if (freshness === 'fresh') return null;
  const age = ageMs === null ? 'and has never been observed' : `and is ${formatDurationMs(ageMs)} old`;
  return freshness === 'unknown'
    ? `${label} is unknown ${age}. Nothing derived from it is being shown as current.`
    : `${label} is stale ${age}.`;
}
